import React, { useEffect, useRef } from 'react';
import { ChatMessage } from '../shared/ChatMessage';
import { ChatInput } from '../shared/ChatInput';
import { AgentAvatar } from '../shared/AgentAvatar';
import { X, Maximize2, MessageSquare } from 'lucide-react';
import { Button } from '../ui/Button';
import { brand } from '../../services/brand';
import { ChatMessage as ChatMessageType } from '../../data/mockData';
interface FloatingChatProps {
  messages: ChatMessageType[];
  isLoading: boolean;
  onSend: (msg: string) => void;
  onExpand: () => void;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}
export function FloatingChat({
  messages,
  isLoading,
  onSend,
  onExpand,
  isOpen,
  setIsOpen
}: FloatingChatProps) {
  const safeMessages = Array.isArray(messages) ? messages : [];
  const chatEndRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to bottom on new messages when open
  useEffect(() => {
    if (isOpen) {
      chatEndRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [safeMessages, isLoading, isOpen]);
  return <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end" data-id="element-4058">
      {/* Chat Popup */}
      {isOpen && <div className="mb-4 w-[400px] h-[520px] bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden animate-in slide-in-from-bottom-10 fade-in duration-300 origin-bottom-right" data-id="element-4059">
          {/* Header */}
          <div className="h-14 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-4 shrink-0" data-id="element-4060">
            <div className="flex items-center gap-3" data-id="element-4061">
              <AgentAvatar size="sm" data-id="element-4062" />
              <div data-id="element-4063">
                <h3 className="font-semibold text-slate-200 text-sm" data-id="element-4064">
                  {brand.name} Chat
                </h3>
                <div className="flex items-center gap-1.5" data-id="element-4065">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" data-id="element-4066"></span>
                  <span className="text-xs text-emerald-400" data-id="element-4067">Online</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1" data-id="element-4068">
              <Button variant="ghost" size="icon" onClick={onExpand} className="text-slate-400 hover:text-slate-200 hover:bg-slate-800 h-8 w-8" title="Expandir a pantalla completa" data-id="element-4069">
                <Maximize2 size={16} data-id="element-4070" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="text-slate-400 hover:text-slate-200 hover:bg-slate-800 h-8 w-8" title="Cerrar chat" data-id="element-4071">
                <X size={18} data-id="element-4072" />
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 bg-slate-950/50 scroll-smooth" data-id="element-4073">
            {safeMessages.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-500" data-id="element-4074">
                <AgentAvatar size="lg" className="mb-4 opacity-50 grayscale" data-id="element-4075" />
                <p className="text-sm" data-id="element-4076">¡Hola! Soy tu asistente virtual.</p>
                <p className="text-xs mt-2" data-id="element-4077">
                  Pregúntame sobre el sprint, tareas o métricas.
                </p>
              </div> : safeMessages.map(msg => <ChatMessage key={msg.id} message={msg} onFollowup={onSend} data-id="element-4078" />)}

            <div ref={chatEndRef} data-id="element-4079" />
          </div>

          {/* Input Area */}
          <div className="p-3 bg-slate-900 border-t border-slate-800 shrink-0" data-id="element-4080">
            <ChatInput onSend={onSend} isLoading={isLoading} data-id="element-4081" />
          </div>
        </div>}

      {/* Floating Action Button (FAB) */}
      <button onClick={() => setIsOpen(!isOpen)} className={`group relative flex items-center justify-center w-14 h-14 rounded-full shadow-lg shadow-indigo-900/40 transition-all duration-300 hover:scale-110 active:scale-95 ${isOpen ? 'bg-slate-800 text-slate-400 rotate-90' : 'bg-indigo-600 text-white hover:bg-indigo-500'}`} data-id="element-4082">
        {isOpen ? <X size={24} data-id="element-4083" /> : <>
            <MessageSquare size={24} className="absolute transition-all duration-300 group-hover:scale-0 group-hover:opacity-0" data-id="element-4084" />

            <AgentAvatar size="md" className="absolute opacity-0 scale-0 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100" data-id="element-4085" />

            {/* Notification Dot (mock logic: show if messages > 0 and closed) */}
            {!isOpen && safeMessages.length > 0 && <span className="absolute top-0 right-0 flex h-4 w-4" data-id="element-4086">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-4087"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 border-2 border-slate-900" data-id="element-4088"></span>
              </span>}
          </>}
      </button>
    </div>;
}